import React, { useEffect } from 'react'
import { Box, DialogContent, Stack } from '@mui/material'
import { FormProvider, SubmitHandler, useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { TFunction } from 'i18next'
import { getDeviceDefaultName } from 'src/Libs/flow/parsers'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'

import { NumberField, Select, TextField } from 'src/Libs/inputs/react-hook-form'
import { UnitTypes } from 'src/Libs/types'
import { PV_METER_MAX_PEAK_POWER, PV_METER_MIN_PEAK_POWER } from '../../../../constants'
import { Meter, MeterDeviceType } from '../../../../powermeter'
import { TextField as ShellTextField } from '@e3dc-react/shell/Elements/InputFields'
import { Alert } from '@e3dc-react/shell/Elements/Alert'
import Actions from '@e3dc-react/shell/Elements/Dialog/ActionDialog/Actions'
import { useDialogActionsPositioned } from '../../../hooks'

const PV_METER_DEFAULT_PEAK_POWER = 9000
//#region AddMeter component
interface AddMeterForm {
  name: string
  deviceType: MeterDeviceType

  /** Use only if deviceType is "PV inverter" */
  pvInverterPeakPower: number
}

interface AddMeterProps {
  error: string | null
  closeDialog: () => void
  meters: Meter[]
  addMeter: (name: string, deviceType: MeterDeviceType, peakPower: number) => void
}

/**
 * Form to add a power meter to the installation. Thought of as content inside a dialog.
 */
export const AddMeter: React.FC<AddMeterProps> = ({ error, closeDialog, meters, addMeter }) => {
  const { t } = useCustomTranslation()

  const methods = useForm<AddMeterForm>({
    defaultValues: {
      name: 'Power meter',
      deviceType: 'PVExtern',
      pvInverterPeakPower: PV_METER_DEFAULT_PEAK_POWER,
    },
    mode: 'onChange',
    resolver: zodResolver(validationSchema),
  })
  const { control, trigger, formState } = methods

  const deviceType = useWatch({ control, name: 'deviceType' })
  const isPvInverter = deviceType === 'PVExtern'

  useEffect(() => {
    trigger()
  }, [isPvInverter, trigger])

  const onSubmit: SubmitHandler<AddMeterForm> = data => {
    addMeter(data.name, data.deviceType, data.pvInverterPeakPower)
    closeDialog()
  }
  const isSaveButtonEnabled = !error && formState.isValid

  return (
    <FormProvider {...methods}>
      <Box component={'form'} display={'contents'} onSubmit={methods.handleSubmit(onSubmit)}>
        <DialogContent dividers sx={{ px: 0, py: 4 }}>
          {error ? (
            <Box>
              <Alert severity="warning" sx={{ marginBottom: 2 }} text={error} />
              <Stack gap={2}>
                {meters.map(meter => (
                  <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }} key={meter.id}>
                    <ShellTextField
                      sx={{ flex: 1 }}
                      label={t('common.name')}
                      value={getDeviceDefaultName(meter.name, 'Meter', meter.deviceId, t)}
                      disabled
                    />
                    <ShellTextField
                      sx={{ flex: 0.25 }}
                      label={t('productEditor.pairingEditor.pairingID.submeter')}
                      value={meter.pairingId}
                      disabled
                    />
                  </Box>
                ))}
              </Stack>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField name="name" label={t('installation.productEditor.deviceName')} required fullWidth minLength={1} />
              <Select name="deviceType" label={t('installation.evseEditor.deviceType')} options={meterOptions(t)} fullWidth />
              {isPvInverter && (
                <NumberField
                  name="pvInverterPeakPower"
                  label={t('installation.submeterEditor.peakPower')}
                  suffix={UnitTypes.wattPeak}
                  inputProps={{
                    min: PV_METER_MIN_PEAK_POWER,
                    max: PV_METER_MAX_PEAK_POWER,
                  }}
                  fullWidth
                />
              )}
            </Box>
          )}
        </DialogContent>
        <Actions
          actions={useDialogActionsPositioned(
            t('installation.productEditor.edit.submeter'),
            methods.handleSubmit(onSubmit),
            !isSaveButtonEnabled,
            closeDialog
          )}
        />
      </Box>
    </FormProvider>
  )
}
//#endregion

//#region Validation and Options
const validationSchema = z.object({
  name: z.string().nonempty(),
  deviceType: z.enum(['HVAC', 'PVExtern', 'Evse', 'House']),
  pvInverterPeakPower: z.coerce.number().min(PV_METER_MIN_PEAK_POWER).max(PV_METER_MAX_PEAK_POWER),
})

const meterOptions = (t: TFunction): Options<MeterDeviceType> => [
  { value: 'HVAC', label: t('installation.submeterEditor.deviceTypeOptions.hvac') },
  { value: 'PVExtern', label: t('installation.submeterEditor.deviceTypeOptions.pv') },
  { value: 'Evse', label: t('installation.submeterEditor.deviceTypeOptions.evse') },
  { value: 'House', label: t('installation.submeterEditor.deviceTypeOptions.house') },
]
